import apiClient from './api-client'

export interface LoginCredentials {
  email: string
  password: string
  remember?: boolean
}

export interface LoginResponse {
  status: boolean
  message: string
  requires_2fa?: boolean
  data: {
    token?: string
    user_id?: number
    user?: {
      id: number
      name: string
      email: string
      created_at: string
    }
  }
}

export interface TwoFactorVerifyRequest {
  user_id: number
  code: string
}

export interface TwoFactorSetupResponse {
  status: boolean
  message: string
  data?: {
    secret: string
    qr_code_url: string
  }
}

export interface TwoFactorStatusResponse {
  status: boolean
  data: {
    two_factor_enabled: boolean
    two_factor_confirmed_at: string | null
  }
}

export interface UserResponse {
  status: boolean
  data: {
    id: number
    name: string
    email: string
    created_at: string
  }
}

export interface ForgotPasswordRequest {
  email: string
}

export interface ResetPasswordRequest {
  email: string
  token: string
  password: string
  password_confirmation: string
}

export interface ApiResponse {
  status: boolean
  message: string
}

class AuthService {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/login', credentials)
    return response.data
  }

  async logout(): Promise<ApiResponse> {
    const response = await apiClient.post<ApiResponse>('/logout')
    return response.data
  }

  async getUser(): Promise<UserResponse> {
    const response = await apiClient.get<UserResponse>('/user')
    return response.data
  }

  async refreshToken(): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/refresh')
    return response.data
  }

  async forgotPassword(data: ForgotPasswordRequest): Promise<ApiResponse> {
    const response = await apiClient.post<ApiResponse>('/forgot-password', data)
    return response.data
  }

  async resetPassword(data: ResetPasswordRequest): Promise<ApiResponse> {
    const response = await apiClient.post<ApiResponse>('/reset-password', data)
    return response.data
  }

  // Two-Factor Authentication methods
  async verify2FA(data: TwoFactorVerifyRequest): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/2fa/verify', data)
    return response.data
  }

  async get2FAStatus(): Promise<TwoFactorStatusResponse> {
    const response = await apiClient.get<TwoFactorStatusResponse>('/2fa/status')
    return response.data
  }

  async setup2FA(): Promise<TwoFactorSetupResponse> {
    const response = await apiClient.post<TwoFactorSetupResponse>('/2fa/setup')
    return response.data
  }

  async enable2FA(code: string): Promise<ApiResponse> {
    const response = await apiClient.post<ApiResponse>('/2fa/enable', { code })
    return response.data
  }

  async disable2FA(code: string, password: string): Promise<ApiResponse> {
    const response = await apiClient.post<ApiResponse>('/2fa/disable', { code, password })
    return response.data
  }

  // Password change
  async changePassword(
    currentPassword: string,
    newPassword: string,
    newPasswordConfirmation: string,
  ): Promise<ApiResponse> {
    const response = await apiClient.post<ApiResponse>('/user/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
      new_password_confirmation: newPasswordConfirmation,
    })
    return response.data
  }
}

export default new AuthService()
